import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  InputAdornment,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import type { PurchaseOrder, PurchaseOrderItem, Supplier, InventoryItem } from '@/types';
import type { WarehouseItem } from '@/hooks/useInventory';

interface PurchaseOrderFormDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder?: PurchaseOrder | null;
  suppliers: Supplier[];
  inventory: InventoryItem[];
  warehouses: WarehouseItem[];
  /**
   * Lines to seed a brand-new order with (create mode only, ignored when
   * editing an existing PO). Lets the inventory "order this part" action open
   * the form with the part already added. #1166
   */
  initialItems?: PurchaseOrderItem[];
  onSave: (data: PurchaseOrderFormData, isDraft: boolean) => void;
}

export interface PurchaseOrderFormData {
  supplierId: string;
  warehouseId: string;
  items: PurchaseOrderItem[];
  shippingCents: number;
  notes?: string;
}

interface FormItem extends Omit<PurchaseOrderItem, 'id'> {
  id: string;
}

// Finnish general VAT since 2024-09-01. Matches the rate the API applies when
// the purchase order is saved (services/staff-api/src/routes/inventory.ts).
const PURCHASE_ORDER_VAT_PERCENT = 25.5;

export function PurchaseOrderFormDialog({
  open,
  onClose,
  purchaseOrder: po,
  suppliers,
  inventory,
  warehouses,
  initialItems,
  onSave,
}: PurchaseOrderFormDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const isEdit = !!po;

  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [items, setItems] = useState<FormItem[]>([]);
  const [shippingCents, setShippingCents] = useState(0);
  const [notes, setNotes] = useState('');

  // Add item form
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItem | null>(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addUnitCost, setAddUnitCost] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form when PO changes
  useEffect(() => {
    if (po) {
      setSupplierId(po.supplierId);
      setWarehouseId(po.warehouseId);
      setItems(
        po.items.map((item) => ({
          ...item,
          id: item.id,
        }))
      );
      setShippingCents(po.shippingCents);
      setNotes(po.notes || '');
    } else {
      resetForm();
      // Create mode: seed lines from initialItems (e.g. the inventory
      // "order this part" action), giving each a stable local id. #1166
      if (initialItems && initialItems.length > 0) {
        setItems(
          initialItems.map((item, idx) => ({
            ...item,
            id: `initial-${idx}`,
          })),
        );
      }
    }
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po, open]);

  const resetForm = () => {
    setSupplierId('');
    setWarehouseId(warehouses.find((w) => w.isDefault)?.id ?? warehouses[0]?.id ?? '');
    setItems([]);
    setShippingCents(0);
    setNotes('');
    setSearchQuery('');
    setSelectedInventoryItem(null);
    setAddQuantity(1);
    setAddUnitCost(0);
  };

  // Filter inventory based on search (not by supplier — parts aren't
  // supplier-scoped in the catalog; any part can be added to any PO).
  const filteredInventory = useMemo(() => {
    let filtered = inventory;

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.partNumber.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query) ||
          item.barcode?.toLowerCase().includes(query)
      );
    }

    return filtered.slice(0, 20); // Limit results
  }, [inventory, searchQuery]);

  // Auto-fill unit cost when item is selected
  useEffect(() => {
    if (selectedInventoryItem) {
      setAddUnitCost(selectedInventoryItem.costPriceCents);
    }
  }, [selectedInventoryItem]);

  const handleAddItem = () => {
    if (!selectedInventoryItem || addQuantity <= 0 || addUnitCost < 0) return;

    const newItem: FormItem = {
      id: `temp-${Date.now()}`,
      inventoryItemId: selectedInventoryItem.id,
      partNumber: selectedInventoryItem.partNumber,
      name: selectedInventoryItem.name,
      description: selectedInventoryItem.description,
      quantity: addQuantity,
      unitCostCents: addUnitCost,
      totalCostCents: Math.round(addQuantity * addUnitCost),
    };

    // Check if item already exists
    const existingIndex = items.findIndex((item) => item.partNumber === selectedInventoryItem.partNumber);
    if (existingIndex >= 0) {
      // Update quantity
      setItems((prev) =>
        prev.map((item, idx) => {
          if (idx === existingIndex) {
            const newQuantity = item.quantity + addQuantity;
            return {
              ...item,
              quantity: newQuantity,
              totalCostCents: Math.round(newQuantity * item.unitCostCents),
            };
          }
          return item;
        })
      );
    } else {
      // Add new item
      setItems((prev) => [...prev, newItem]);
    }

    // Reset add form
    setSelectedInventoryItem(null);
    setAddQuantity(1);
    setAddUnitCost(0);
    setSearchQuery('');
    setIsDirty(true);
  };

  const handleUpdateItemQuantity = (id: string, quantity: number) => {
    if (quantity < 0) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            quantity,
            totalCostCents: Math.round(quantity * item.unitCostCents),
          };
        }
        return item;
      })
    );
    setIsDirty(true);
  };

  const handleUpdateItemCost = (id: string, unitCostCents: number) => {
    if (unitCostCents < 0) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            unitCostCents,
            totalCostCents: Math.round(item.quantity * unitCostCents),
          };
        }
        return item;
      })
    );
    setIsDirty(true);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setIsDirty(true);
  };

  // Calculate totals
  const subtotalCents = items.reduce((sum, item) => sum + item.totalCostCents, 0);
  const taxCents = Math.round(subtotalCents * (PURCHASE_ORDER_VAT_PERCENT / 100));
  const totalCents = subtotalCents + taxCents + shippingCents;

  const canSave = supplierId && warehouseId && items.length > 0;

  const handleSave = (isDraft: boolean) => {
    if (!canSave) return;

    const data: PurchaseOrderFormData = {
      supplierId,
      warehouseId,
      items: items.map((item) => ({
        id: item.id,
        inventoryItemId: item.inventoryItemId,
        partNumber: item.partNumber,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unitCostCents: item.unitCostCents,
        totalCostCents: item.totalCostCents,
      })),
      shippingCents,
      notes: notes || undefined,
    };

    onSave(data, isDraft);
    handleFormClose();
  };

  const handleFormClose = () => {
    if (!isEdit) {
      resetForm();
    }
    setIsDirty(false);
    onClose();
  };

  const selectedSupplier = suppliers.find((s) => s.id === supplierId);

  return (
    <FormDialog
      open={open}
      onClose={handleFormClose}
      title={isEdit ? t('purchaseOrderForm.editTitle') : t('purchaseOrderForm.addTitle')}
      isDirty={isDirty}
      maxWidth="lg"
      renderActions={({ onClose: handleCloseAction }) => (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAction}>{tc('cancel')}</Button>
          <Button variant="outlined" onClick={() => { handleSave(true); }} disabled={!canSave}>
            {t('purchaseOrderForm.saveDraft')}
          </Button>
          <Button variant="contained" onClick={() => { handleSave(false); }} disabled={!canSave}>
            {isEdit ? tc('save') : t('purchaseOrderForm.submitForApproval')}
          </Button>
        </DialogActions>
      )}
    >
      <DialogContent dividers>
        {/* Supplier + Warehouse Selection */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('purchaseOrderForm.supplierRequired')}
            </Typography>
            <Autocomplete
              value={selectedSupplier || null}
              onChange={(_, newValue) => { setSupplierId(newValue?.id || ''); setIsDirty(true); }}
              options={suppliers}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('purchaseOrderForm.deliveryTime', { days: option.deliveryDays })}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} autoFocus placeholder={t('purchaseOrderForm.selectSupplier')} />}
              disabled={isEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('purchaseOrderForm.warehouseRequired')}
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>{t('purchaseOrderForm.warehouseLabel')}</InputLabel>
              <Select
                value={warehouseId}
                label={t('purchaseOrderForm.warehouseLabel')}
                onChange={(e) => { setWarehouseId(e.target.value); setIsDirty(true); }}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Add Items Section */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('purchaseOrderForm.addParts')}
        </Typography>

        {!supplierId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('purchaseOrderForm.selectSupplierFirst')}
          </Alert>
        )}

        {supplierId && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('purchaseOrderForm.partLabel')}
              </Typography>
              <Autocomplete
                value={selectedInventoryItem}
                onChange={(_, newValue) => setSelectedInventoryItem(newValue)}
                options={filteredInventory}
                getOptionLabel={(option) => `${option.partNumber} - ${option.name}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.partNumber} - {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('purchaseOrderForm.stockInfo', { qty: option.quantity, price: (option.costPriceCents / 100).toFixed(2) })}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('purchaseOrderForm.searchPart')}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                noOptionsText={tc('noResults')}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('purchaseOrderForm.quantityLabel')}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={addQuantity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value.replace(',', '.'));
                  setAddQuantity(isNaN(val) || val < 0 ? 0 : val);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{t('purchaseOrderDetail.pcs')}</InputAdornment>,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('purchaseOrderForm.costPerUnit')}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={addUnitCost / 100}
                onChange={(e) => setAddUnitCost(Math.max(0, Math.round(parseFloat(e.target.value) * 100) || 0))}
                inputProps={{ min: 0, step: 0.01 }}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ visibility: 'hidden' }}>
                {tc('add')}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={!selectedInventoryItem || addQuantity <= 0}
                sx={{ height: 56 }}
              >
                {tc('add')}
              </Button>
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Items Table */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('purchaseOrderForm.itemsToOrder', { count: items.length })}
        </Typography>

        {items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('purchaseOrderForm.noItemsAdded')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('purchaseOrderForm.colPartNumber')}</TableCell>
                  <TableCell>{t('purchaseOrderForm.colName')}</TableCell>
                  <TableCell align="right">{t('purchaseOrderForm.colQuantity')}</TableCell>
                  <TableCell align="right">{t('purchaseOrderForm.colUnitPrice')}</TableCell>
                  <TableCell align="right">{t('purchaseOrderForm.colTotal')}</TableCell>
                  <TableCell align="center">{t('purchaseOrderForm.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                        {item.partNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.name}</Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value.replace(',', '.'));
                          handleUpdateItemQuantity(item.id, isNaN(val) || val < 0 ? 0 : val);
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.unitCostCents / 100}
                        onChange={(e) => handleUpdateItemCost(item.id, Math.round(parseFloat(e.target.value) * 100) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 100 }}
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        €{(item.totalCostCents / 100).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={tc('delete')}>
                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)} aria-label={tc('delete')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Cost Summary */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('purchaseOrderForm.notesLabel')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setIsDirty(true); }}
              placeholder={t('purchaseOrderForm.notesPlaceholder')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('purchaseOrderForm.subtotal')}</Typography>
                  <Typography variant="body2">€{(subtotalCents / 100).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('purchaseOrderForm.vat', { rate: String(PURCHASE_ORDER_VAT_PERCENT).replace('.', ',') })}</Typography>
                  <Typography variant="body2">€{(taxCents / 100).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{t('purchaseOrderForm.shipping')}</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={shippingCents / 100}
                    onChange={(e) => { setShippingCents(Math.max(0, Math.round(parseFloat(e.target.value) * 100) || 0)); setIsDirty(true); }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ width: 120 }}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      },
                    }}
                  />
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{t('purchaseOrderForm.total')}</Typography>
                  <Typography variant="h6" color="primary">
                    €{(totalCents / 100).toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

    </FormDialog>
  );
}
